/**
 * AI-generated code, moderated by lzygmanski
 */
import type {ColumnDef} from '@tanstack/react-table';

import {Input} from '@/components/ui/input';
import type {HealthLogMetricDefinition} from '@/services/graphqlHooks';
import healthLogMetricsFields from '@/translation/healthLogMetricsFields';

import type {ResidentMeasurementRow} from '../types/healthLogMeasurementsDialog';

type BuildColumnsParams = {
  t: (key: string) => string;
  selectedMetric: HealthLogMetricDefinition | undefined;
  onValueChange: (residentId: string, fieldName: string, value: string) => void;
};

export function buildHealthLogMeasurementsColumns({
  t,
  selectedMetric,
  onValueChange,
}: BuildColumnsParams): ColumnDef<ResidentMeasurementRow>[] {
  const baseColumns: ColumnDef<ResidentMeasurementRow>[] = [
    {
      // Hidden column for search - personal number
      accessorFn: row => row.resident.personalNumber,
      id: 'personalNumber',
      header: t('Personal Number'),
      enableGlobalFilter: true,
      enableSorting: false,
      cell: () => null, // Hidden from UI
    },
    {
      accessorFn: row =>
        `${row.resident.name.firstName} ${row.resident.name.lastName}`,
      id: 'name',
      header: t('Name'),
      cell: ({row}) => {
        const {firstName, middleName, lastName} = row.original.resident.name;
        return [firstName, middleName, lastName].filter(Boolean).join(' ');
      },
    },
    {
      accessorFn: row => row.resident.roomNumber,
      id: 'roomNumber',
      header: t('Room'),
      cell: ({row}) => row.original.resident.roomNumber,
    },
  ];

  if (!selectedMetric) return baseColumns;

  // Add columns for each metric field
  const fieldColumns: ColumnDef<ResidentMeasurementRow>[] =
    selectedMetric.fields.map(field => ({
      id: `field_${field.name}`,
      header: () => {
        const fieldLabel = healthLogMetricsFields[field.name] || field.name;
        return field.unit ? `${fieldLabel} (${field.unit})` : fieldLabel;
      },
      enableGlobalFilter: false,
      enableSorting: false,
      cell: ({row}) => {
        const residentId = row.original.resident.id;
        const value = row.original.values[field.name] ?? '';

        return (
          <Input
            type="number"
            step="any"
            placeholder="-"
            value={value}
            onChange={e => {
              onValueChange(residentId, field.name, e.target.value);
            }}
            className="w-full"
          />
        );
      },
    }));

  return [...baseColumns, ...fieldColumns];
}
